import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useOs } from '../../../api/queries/ordensDeServico'
import { useSessao } from '../../../auth/contexto'
import { Abas } from '../../../components/Abas'
import { AvisoDeErro, Esqueleto } from '../../../components/Estados'
import { IconeImprimir, IconeSeta } from '../../../components/Icones'
import { dataHora } from '../../../lib/datas'
import { podeEscreverOs } from '../acessoDaOs'
import { SeloDaSituacao } from '../SeloDaSituacao'
import { OS } from '../textosDaOs'
import { AcoesDaOs, AvisoDeRevogacao } from './AcoesDaOs'
import { AndamentoDaOs } from './AndamentoDaOs'
import { IdentificacaoDaOs } from './IdentificacaoDaOs'
import { ItensDaOs } from './ItensDaOs'
import { NotaDaDivisao } from './NotaDaDivisao'
import { PrazosDaOs } from './PrazosDaOs'

/**
 * Uma ordem de serviço, em três abas.
 *
 * **Não há botão de editar, e não é esquecimento.** A ordem autoriza despesa e é o marco de que o
 * contrato conta prazo; corrigi-la por cima apagaria qual texto autorizou o quê. Quem se enganou
 * revoga — fica quem revogou, quando e por quê — e registra a correta ao lado. No banco a regra é a
 * mesma: o trigger da V33 compara a LINHA INTEIRA e recusa qualquer `UPDATE` que não seja a
 * revogação, inclusive o feito à mão no psql.
 *
 * As abas separam três perguntas que não se respondem juntas: quem pediu e o que foi pedido
 * (identificação), quanto e por que dá esse valor (itens), e o que já aconteceu — com os prazos que
 * daí saem (andamento).
 *
 * @returns a tela da ordem
 */
export function PaginaOrdemDeServico() {
  const { id = '' } = useParams()
  const identificador = Number(id)
  const { data: ordem, isPending, isError, error } = useOs(identificador)
  const { usuario } = useSessao()
  const [aba, definirAba] = useState('identificacao')

  if (isPending) {
    return (
      <div className="cartao px-4 py-5">
        <Esqueleto linhas={8} />
      </div>
    )
  }
  if (isError || !ordem) {
    return <AvisoDeErro erro={error} titulo="Não foi possível abrir a ordem de serviço" />
  }

  return (
    <div className="space-y-4">
      <Link
        to="/ordens-servico"
        className="inline-flex items-center gap-1.5 text-sm text-niteroi-700 underline underline-offset-2"
      >
        <IconeSeta className="h-4 w-4 rotate-180" />
        {OS.voltarParaALista}
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="titulo-da-pagina tabular-nums">{ordem.numero}</h1>
          {ordem.numeroExterno && (
            <p className="text-sm text-slate-600">
              {OS.numeroExterno}: <span className="tabular-nums">{ordem.numeroExterno}</span>
            </p>
          )}
          <p className="mt-1 text-xs text-slate-500">
            {OS.registradaPor(ordem.registradoPorNome, dataHora(ordem.registradoEm))}
          </p>
          <div className="mt-2">
            <SeloDaSituacao situacao={ordem.situacao} comEspera />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/ordens-servico/${ordem.id}/documento`}>
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50">
              <IconeImprimir className="h-4 w-4" />
              {OS.verDocumento}
            </span>
          </Link>
          <AcoesDaOs ordem={ordem} podeEscrever={podeEscreverOs(usuario)} />
        </div>
      </header>

      {ordem.revogacao && <AvisoDeRevogacao revogacao={ordem.revogacao} />}

      <Abas
        rotulo="Seções da ordem de serviço"
        ativa={aba}
        aoMudar={definirAba}
        abas={[
          { id: 'identificacao', rotulo: OS.abas.identificacao },
          { id: 'itens', rotulo: OS.abas.itens, contador: ordem.itens.length },
          { id: 'andamento', rotulo: OS.abas.andamento, contador: ordem.fatos.length },
        ]}
      >
        {aba === 'identificacao' && <IdentificacaoDaOs ordem={ordem} />}
        {aba === 'itens' && (
          <div className="space-y-4">
            <ItensDaOs ordem={ordem} />
            <NotaDaDivisao regime={ordem.regime} />
          </div>
        )}
        {aba === 'andamento' && (
          <div className="space-y-4">
            <PrazosDaOs id={ordem.id} />
            <AndamentoDaOs ordem={ordem} podeEscrever={podeEscreverOs(usuario)} />
          </div>
        )}
      </Abas>
    </div>
  )
}
